import debounce from 'lodash.debounce';
import { sendSysUI } from './common';
import { ANALYTICS_RATE_LIMIT_MS } from '@wwe-portal/web/utils/config';

const FLOW_NAME = 'superstar_guide';

export interface AnalyticsFilterParams {
  playStyle: string | null;
  era: string | null;
  classValue: string | null;
  owned: boolean;
}

export interface AnalyticsSearchParams {
  searchText: string;
  searchEnabled: boolean;
  searchResultCount: number;
}

const isFilterApplied = (filter: AnalyticsFilterParams) => {
  return (
    filter.playStyle != null ||
    filter.era != null ||
    filter.classValue != null ||
    filter.owned
  );
};

const getFilterAppliedParams = (filter: AnalyticsFilterParams) => {
  return {
    default_view: isFilterApplied(filter) ? 'N' : 'Y',
    filters_applied: filter,
  };
};

const getSearchAndFilterParams = (
  search: AnalyticsSearchParams,
  filter: AnalyticsFilterParams
) => {
  return {
    ...getFilterAppliedParams(filter),
    search: {
      search_applied: search.searchEnabled ? 'Y' : 'N',
      search_terms: search.searchText,
      num_results_returned: search.searchResultCount,
    },
  };
};

// 1. The page is loaded as initial load, or is load after the user applies filters or search
export const sendTrendingSuperstarLoadEvent = debounce(
  (filter: AnalyticsFilterParams) => {
    sendSysUI({
      flow_name: FLOW_NAME,
      interaction_type: 'load',
      screen_content: JSON.stringify(getFilterAppliedParams(filter)),
      interaction_object_category: null,
      flow_order: null,
      screen_subcategory: null,
      screen_type: 'screen',
    });
  },
  ANALYTICS_RATE_LIMIT_MS
);

// 2. Search results page is loaded
export const sendSuperstarSearchResultLoadEvent = debounce(
  (search: AnalyticsSearchParams, filter: AnalyticsFilterParams) => {
    sendSysUI({
      flow_name: FLOW_NAME,
      interaction_type: 'load',
      screen_content: JSON.stringify(getSearchAndFilterParams(search, filter)),
      interaction_object_category: null,
      flow_order: null,
      screen_subcategory: null,
      screen_type: 'screen',
    });
  },
  ANALYTICS_RATE_LIMIT_MS
);

// 3. User clicks the flip icon of a SS both in Trending section and details page
export const sendTrendingSupersstarFlipButtonClickEvent = debounce(
  (
    superstarId: string,
    screenCategory: 'superstar_guide_trending' | 'superstar_details'
  ) => {
    sendSysUI({
      flow_name: FLOW_NAME,
      interaction_type: 'tap',
      interaction_object: 'flipping_icon',
      screen_content: JSON.stringify({
        superstar_id: superstarId,
      }),
      screen_category: screenCategory,
      interaction_object_category: null,
      flow_order: null,
      screen_subcategory: null,
      screen_type: 'screen',
    });
  },
  ANALYTICS_RATE_LIMIT_MS
);

// 4. Superstar details page is loaded
export const sendSuperstarDetailsLoadEvent = debounce((superstarId: string) => {
  sendSysUI({
    flow_name: FLOW_NAME,
    interaction_type: 'load',
    screen_content: JSON.stringify({
      superstar_id: superstarId,
    }),
    interaction_object_category: null,
    flow_order: null,
    screen_subcategory: null,
    screen_type: 'screen',
  });
}, ANALYTICS_RATE_LIMIT_MS);

// 5. User expands any of the sections inside the superstars details page
export const sendSuperstarDetailsSectionExpandEvent = debounce(
  (superstarId: string, objectName: string) => {
    sendSysUI({
      flow_name: FLOW_NAME,
      interaction_type: 'tap',
      interaction_object: objectName,
      screen_content: JSON.stringify({
        superstar_id: superstarId,
      }),
      interaction_object_category: null,
      flow_order: null,
      screen_subcategory: null,
      screen_type: 'screen',
    });
  },
  ANALYTICS_RATE_LIMIT_MS
);

// 6. When a filter or a search is applied in the main page the user sees the "superstar", "moves", "entourage" and "offers" sections. This trigger is fired when one of these sections is expanded
export const sendSuperstarSearchSectionExpandEvent = debounce(
  (
    search: AnalyticsSearchParams,
    filter: AnalyticsFilterParams,
    objectName: string
  ) => {
    sendSysUI({
      flow_name: FLOW_NAME,
      interaction_type: 'tap',
      interaction_object: objectName,
      screen_content: JSON.stringify(getSearchAndFilterParams(search, filter)),
      interaction_object_category: null,
      flow_order: null,
      screen_subcategory: null,
      screen_type: 'screen',
    });
  },
  ANALYTICS_RATE_LIMIT_MS
);

// 7. User clicks the Set Base button in details page to compare the stats between levels
export const sendSuperstarSetBaseButtonClickEvent = debounce(
  (superstarId: string) => {
    sendSysUI({
      flow_name: FLOW_NAME,
      interaction_type: 'tap',
      interaction_object: 'set_base_button',
      screen_content: JSON.stringify({
        superstar_id: superstarId,
      }),
      interaction_object_category: null,
      flow_order: null,
      screen_subcategory: null,
      screen_type: 'screen',
    });
  },
  ANALYTICS_RATE_LIMIT_MS
);
